import React from "react";
import { Combobox, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { Product } from "../../utils/types";
import { useAllProducts } from "../../hooks/useAllProducts";
import { Link } from "gatsby";
import { useOptions } from "../../hooks/useOptions";
import { __t } from "@/utils/translate";

type SearchComboboxProps = {
    className?: string;
    closeModal: () => void;
};

const SearchCombobox: React.FC<SearchComboboxProps> = ({ className = "", closeModal }) => {
    const allProducts = useAllProducts();
    const products = allProducts.allWcProducts.edges.map((edge) => edge.node);

    const [selected, setSelected] = useState("");
    const [query, setQuery] = useState("");

    const filteredProducts =
        query === ""
            ? products
            : products.filter((product) =>
                product.name
                    .toLowerCase()
                    .replace(/\s+/g, "")
                    .includes(query.toLowerCase().replace(/\s+/g, ""))
            );

    return (
        <div
            className={`${className} h-auto w-full bg-light-gray font-verano text-base-thin z-[100]`}
        >
            <Combobox value={selected} onChange={setSelected}>
                <div className="relative">
                    <div className="relative flex w-full cursor-default items-center overflow-hidden border-none text-left">
                        <Combobox.Input
                            className="bg-search-input text-18 h-[50px] w-full border-none outline-none py-2 pl-[48px] pr-10 placeholder:text-dark/80"
                            placeholder={__t('Search products...')}
                            displayValue={(product: Product) => product.name}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                        <button aria-label="search close" onClick={() => closeModal()} className='absolute top-4 right-4'>
                            <svg width="18px" height="18px" viewBox="-0.5 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 21.32L21 3.32001" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M3 3.32001L21 21.32" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery("")}
                    >
                        <Combobox.Options className={`${filteredProducts.length === 0 && query !== "" ? 'max-h-10' : 'max-h-[460px]'} bg-white border-b border-stroke-color z-[100] flex w-full flex-col gap-[20px] overflow-auto px-[20px] py-[24px]`}>
                            {filteredProducts.length === 0 && query !== "" ? (
                                <div className="relative cursor-default select-none px-[16px] py-1">
                                    {__t("No products were found matching your selection.")}
                                </div>
                            ) : (
                                filteredProducts.map((product) => (
                                    <Combobox.Option
                                        key={product.id}
                                        className="relative select-none"
                                        value={product}
                                    >
                                        <Link to={new URL(product.permalink).pathname} className="cursor-pointer">
                                            <ProductCard product={product} />
                                        </Link>
                                    </Combobox.Option>
                                ))
                            )}
                        </Combobox.Options>
                    </Transition>
                </div>
            </Combobox>
        </div>
    );
};

type ProductCardProps = {
    product: Product;
};

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
    const options = useOptions();
    const currency_symbol = options.wpOptions.data.currency_symbol;

    return (
        <div className="flex font-verano group">
            <div className="flex w-full">
                <div className="flex gap-[8px] xl:gap-[32px]">
                    <img
                        className="h-[74px] w-[74px]"
                        src={product.image.src}
                        alt={product.image.alt}
                    />
                    <div className="flex max-w-[306px] flex-col items-start">
                        <p className="mb-[8px] text-left text-base-16-med text-dark-gray group-hover:underline">
                            {product.name}
                        </p>
                        <span className="text-base-16-med text-light-red">
                            <div className="inline" dangerouslySetInnerHTML={{ __html: currency_symbol }} ></div> {product.price || ""}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchCombobox;
